import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryParam } from '../../../utils/hooks/useQueryParam';

/** The address parameter of the open panel: `/app/home?whatsNew=10.1.0`. */
export const WHATS_NEW_PARAM = 'whatsNew';

export interface WhatsNewUrl {
  /** The version in the address, or null when no panel is open. */
  urlVersion: string | null;
  /** Opens the panel: a new history entry, so Back closes it. */
  open: (version: string) => void;
  /** Closes the panel: Back when this page made the entry, else the parameter goes with no new entry. */
  close: () => void;
}

function withVersion(search: string, version: string | null): string {
  const params = new URLSearchParams(search);
  if (version === null) {
    params.delete(WHATS_NEW_PARAM);
  } else {
    params.set(WHATS_NEW_PARAM, version);
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/**
 * Keeps the open panel in the address (the rule "Web navigation must work"): a reload opens the
 * same panel again, and Back closes it.
 */
export function useWhatsNewUrl(): WhatsNewUrl {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const value = useQueryParam(WHATS_NEW_PARAM, undefined, '');
  // True while the entry on top of the history is the one that `open` pushed.
  const pushed = useRef(false);
  useEffect(() => {
    // Back (or any other way) took the parameter away: the pushed entry is gone.
    if (value === '') {
      pushed.current = false;
    }
  }, [value]);

  const open = useCallback(
    (version: string) => {
      pushed.current = true;
      // eslint-disable-next-line no-void
      void navigate({ pathname, search: withVersion(search, version) });
    },
    [navigate, pathname, search]
  );

  const close = useCallback(() => {
    if (pushed.current) {
      pushed.current = false;
      // eslint-disable-next-line no-void
      void navigate(-1);
      return;
    }
    // eslint-disable-next-line no-void
    void navigate({ pathname, search: withVersion(search, null) }, { replace: true });
  }, [navigate, pathname, search]);

  return { urlVersion: value === '' ? null : value, open, close };
}
